import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { getCatalogSearchResults } from '~/features/catalog/controllers/get-catalog-search-results';
import { useTrackCatalogSearchCompleted } from '~/features/catalog/hooks/use-track-catalog-search-completed';
import { CatalogSearchResults } from '~/features/catalog/models/catalog-search-result';
import { filtersSchema, sortNameSchema, UriFilter } from '~/features/catalog/models/filter';
import { getPageNumber } from '~/features/catalog/utils/get-page-number';
import { isInitialView } from '~/features/catalog/utils/is-initial-view';
import { useFavorites } from '~/features/favorites/hooks/use-favorites';
import { useEnrollmentsProgress } from '~/features/program/hooks/use-enrollments-progress';
import { useI18n } from '~/hooks/use-i18n';

import { INLINE_SEARCH_SURFACE } from '../constants';
import { useCatalogSearchOrigin } from '../utils/catalog-search-draft';

const DEFAULT_ALL_FILTERS: Record<string, string> = {
  scope: 'all',
  language: 'all',
} as const;

export function useCatalogSearchResults({
  initialCatalogSearchResults,
  schoolUriFilters,
  skillUriFilters,
}: {
  initialCatalogSearchResults: CatalogSearchResults | null;
  schoolUriFilters: UriFilter[];
  skillUriFilters: UriFilter[];
}) {
  const router = useRouter();
  const { isTranslated } = useI18n();
  const { favorites, favoritesIsFetched } = useFavorites();
  const { enrollmentsProgressIsLoading, enrollmentsProgress, enrollmentsProgressIsFetched } = useEnrollmentsProgress();
  const searchOrigin = useCatalogSearchOrigin();

  const { selectedFilters, selectedSort, page, searchValue } = useMemo(() => {
    const {
      scope,
      price,
      school,
      skill,
      difficulty,
      duration,
      rawDuration,
      type,
      availability,
      language,
      sort: sortParam,
      page: pageCount,
    } = router.query;

    return {
      selectedFilters: filtersSchema.parse({
        scope,
        price,
        school,
        skill,
        difficulty,
        duration,
        rawDuration,
        type,
        availability,
        // Only apply the language filter when the user's language has translations
        language: isTranslated ? language ?? DEFAULT_ALL_FILTERS.language : undefined,
      }),
      selectedSort: sortNameSchema.parse(sortParam),
      page: getPageNumber(pageCount),
      searchValue: router.query.searchValue as string | undefined,
    };
  }, [router.query, isTranslated]);

  const favoriteFilterSelected = selectedFilters.scope?.[0] === 'favorite';
  const enrolledFilterSelected = selectedFilters.scope?.[0] === 'enrolled';
  const completedFilterSelected = selectedFilters.scope?.[0] === 'completed';
  const companySlug = router.query.companySlug as string | undefined;

  const completedKeys =
    enrollmentsProgress?.programs
      .filter((enrollmentProgress) => enrollmentProgress.complete && enrollmentProgress.completion === 100)
      .map((enrollmentProgress) => enrollmentProgress.key) ?? [];

  // Wait for enrollments/favorites before querying a scope that filters by them.
  const isCatalogSearchEnabled =
    !(completedFilterSelected && !enrollmentsProgressIsFetched) &&
    !(favoriteFilterSelected && !favoritesIsFetched) &&
    (completedFilterSelected ? completedKeys.length > 0 : true) &&
    (favoriteFilterSelected ? !!favorites?.length : true);

  const {
    data: catalogSearchResults,
    isFetching: isFetchingCatalogSearchResults,
    refetch: refetchCatalogSearchResults,
  } = useQuery<CatalogSearchResults>(
    ['catalog', selectedFilters, selectedSort, page, searchValue],
    async () => {
      let scopedKeysToFilterBy: string[] | undefined;

      if (completedFilterSelected) {
        scopedKeysToFilterBy = completedKeys;
      }

      if (favoriteFilterSelected) {
        scopedKeysToFilterBy = favorites;
      }

      return await getCatalogSearchResults({
        selectedFilters,
        selectedSort,
        page,
        schoolUriFilters,
        skillUriFilters,
        searchValue,
        keysToFilterBy: scopedKeysToFilterBy,
        companySlug,
        shouldFetchEnrolledOnly: enrolledFilterSelected || completedFilterSelected,
      });
    },
    {
      enabled: isCatalogSearchEnabled,
      initialData: () => {
        return isInitialView(router.query, searchValue) ? initialCatalogSearchResults ?? undefined : undefined;
      },
    }
  );

  // Boolean outside the effect so the refetch doesn't fire every time the favorites length changes.
  const shouldRefetchFavorites = favoriteFilterSelected && !!favorites?.length;
  useEffect(() => {
    if (shouldRefetchFavorites) {
      refetchCatalogSearchResults();
    }
  }, [shouldRefetchFavorites, refetchCatalogSearchResults]);

  const numberOfSelectedFilters = Object.entries(selectedFilters).filter(
    ([filterType, filterValues]) =>
      filterValues &&
      filterValues.length > 0 &&
      // Exclude filters set to their default value 'all'
      !filterValues.includes(DEFAULT_ALL_FILTERS[filterType])
  ).length;

  useTrackCatalogSearchCompleted({
    searchTerm: searchValue,
    resultCount: catalogSearchResults?.searchResultItems ? catalogSearchResults.numberOfHits : undefined,
    searchSurface: INLINE_SEARCH_SURFACE,
    searchOrigin,
    isSettled: numberOfSelectedFilters === 0 && !isFetchingCatalogSearchResults,
  });

  return {
    catalogSearchResults,
    isFetchingCatalogSearchResults,
    isLoading: isFetchingCatalogSearchResults || enrollmentsProgressIsLoading,
    numberOfSelectedFilters,
    page,
    searchValue,
    selectedFilters,
    totalPages: catalogSearchResults?.numberOfPages ?? 0,
  };
}
